import { api } from 'api';
import { ClaimRow, ExhibitHistory } from 'api/endpoints';
import useGraphql from 'api/graphql/useGraphql';
import { useAppSelector } from 'hooks/reducerHooks';
import _, { has, isNil } from 'lodash';
import { useClaimResolutionTypeContext } from 'providers/ClaimResolutionTypeProvider';
import { useCallback, useMemo, useState } from 'react';
import { isNotEmpty } from 'utils/lodashEx';
import { apiExhibitExport } from '../api/apiExhibitExporter';
import { exportExhibitExcelFile } from '../templates/excelExporter';
import {
  emptyFooter,
  ExhibitFields,
  FootnoteAppear,
  toFootnoteAppear,
  toStructureFooter,
  toStructureHeader,
} from '../typesExhibitExporter';
import { getClaimNumber } from '../utils/field';

const defaultValue: ExhibitFields = {
  exhibitNumber: '',
  objectionBasis: '',
  footer: [emptyFooter],
  structure: {
    header: [],
    footer: [],
    displayObjection: true,
  },
};

export const useExhibitExporterProvider = () => {
  const [client] = useGraphql();
  const { projectDetails } = useAppSelector((s) => s.selectable.data);
  const resolutionType = useClaimResolutionTypeContext();

  const [value, setValue] = useState<ExhibitFields>(defaultValue);
  const [generating, setGenerating] = useState(false);

  const [showAddClaimPopup, setShowAddClaimPopup] = useState(false);

  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [selectedClaims, setSelectedClaims] = useState<ClaimRow[]>([]);

  const [historyShow, setHistoryShow] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExhibits, setHistoryExhibits] = useState<ExhibitHistory[]>([]);

  const createExhibitFile = useCallback(
    async (exhibit: ExhibitFields, claimsToExport: ClaimRow[]) => {
      const { primaryCaseNumber, projectName } = projectDetails ?? {};
      const actionName = resolutionType.actionNames.find(
        (a) => a.id === exhibit.resolutionActionNameId,
      )?.actionName;

      const claimItems = _.sortBy(claimsToExport, ['counterpartyName']);

      await exportExhibitExcelFile({
        exhibit,
        claimItems,
        primaryCaseNumber,
        projectName,
        actionName,
      });
    },
    [projectDetails, resolutionType.actionNames],
  );

  const loadGridRows = useCallback(
    async (resolutionActionNameId: ExhibitFields['resolutionActionNameId']) => {
      if (!resolutionActionNameId) {
        setClaims([]);
        return;
      }

      setClaimsLoading(true);
      const result = await apiExhibitExport.getClaimsByActionName(
        client,
        resolutionActionNameId,
      );

      setClaims(result);
      setClaimsLoading(false);
    },
    [client],
  );

  const updateValue = useCallback(
    <T extends keyof ExhibitFields>(key: T, value: ExhibitFields[T]) => {
      let newValue: Partial<ExhibitFields> = { [key]: value };

      if (key === 'resolutionTypeId')
        newValue = {
          ...newValue,
          resolutionSubStatusId: undefined,
          resolutionActionNameId: undefined,
        };

      if (key === 'resolutionSubStatusId')
        newValue = { ...newValue, resolutionActionNameId: undefined };

      setValue((r) => ({ ...r, ...newValue }));

      if (has(newValue, 'resolutionActionNameId')) {
        loadGridRows(newValue.resolutionActionNameId);
      }
    },
    [loadGridRows],
  );

  const addClaims = useCallback(
    (items: ClaimRow[]) => {
      const newClaims = [...claims, ...items];
      setClaims(newClaims);
    },
    [claims],
  );

  const deleteSelectedClaims = useCallback(() => {
    const selectedIds = selectedClaims.map((c: ClaimRow) => c.id);
    const newClaims = claims.filter((r) => !selectedIds.includes(r.id));
    setClaims(newClaims);
    setSelectedClaims([]);
  }, [claims, selectedClaims]);

  const hasEmptyClaims = useMemo(
    () => claims.some((c) => isNil(getClaimNumber(c))),
    [claims],
  );

  const readyToExport = useMemo(() => {
    return (
      !!value.exhibitNumber &&
      !!value.objectionBasis &&
      !!value.resolutionTypeId &&
      !!claims.length &&
      !hasEmptyClaims
    );
  }, [
    hasEmptyClaims,
    claims.length,
    value.exhibitNumber,
    value.objectionBasis,
    value.resolutionTypeId,
  ]);

  const historyLoad = useCallback(async () => {
    setHistoryLoading(true);
    const result = await api.exhibits.getAll();
    setHistoryExhibits(result ?? []);
    setHistoryLoading(false);
  }, []);

  const toggleHistoryShow = useCallback(() => {
    const newValue = !historyShow;
    setHistoryShow(newValue);

    if (newValue) historyLoad();
  }, [historyLoad, historyShow]);

  const historyCopy = useCallback(
    async (id: number) => {
      const historyItem = historyExhibits.find((h) => h.id === id);
      if (!historyItem) return;

      setClaimsLoading(true);

      const newClaims = await apiExhibitExport.loadClaimRowData(
        client,
        historyItem.claims,
      );
      const exhibit = historyToExhibit(historyItem);

      setValue(exhibit);
      setClaims(newClaims);
      setClaimsLoading(false);
    },
    [client, historyExhibits],
  );

  const historyDownload = useCallback(
    async (id: number) => {
      const historyItem = historyExhibits.find((h) => h.id === id);
      if (!historyItem) return;

      setGenerating(true);
      const exhibit = historyToExhibit(historyItem);
      const claimItems = await apiExhibitExport.loadClaimRowData(
        client,
        historyItem.claims,
      );

      await createExhibitFile(exhibit, claimItems);
      setGenerating(false);
    },
    [client, createExhibitFile, historyExhibits],
  );

  const historyDelete = useCallback(
    async (id: number) => {
      setHistoryLoading(true);
      await api.exhibits.delete(id);
      setHistoryLoading(false);

      await historyLoad();
    },
    [historyLoad],
  );

  const runExhibitExport = useCallback(async () => {
    setGenerating(true);

    const claimItems = isNotEmpty(selectedClaims) ? selectedClaims : claims;
    await apiExhibitExport.saveToHistoryItem(value, claimItems);
    await createExhibitFile(value, claimItems);

    setGenerating(false);

    if (historyShow) historyLoad();
  }, [selectedClaims, claims, value, historyShow, historyLoad, createExhibitFile]);

  return {
    value,
    generating,
    claims,
    showAddClaimPopup,
    selectedClaims,
    claimsLoading,
    hasEmptyClaims,
    readyToExport,
    historyShow,
    historyLoading,
    historyExhibits,

    updateValue,
    addClaims,
    setSelectedClaims,
    deleteSelectedClaims,
    setShowAddClaimPopup,
    toggleHistoryShow,
    historyLoad,
    historyCopy,
    historyDelete,
    historyDownload,
    runExhibitExport,
  };
};

const historyToExhibit = (historyItem: ExhibitHistory): ExhibitFields => {
  const result: ExhibitFields = {
    exhibitNumber: historyItem.exhibitNumber,
    objectionBasis: historyItem.objectionBasis,
    resolutionTypeId: historyItem.actionName.resolutionTypeId,
    resolutionSubStatusId: historyItem.actionName.actionSubStatusId,
    resolutionActionNameId: historyItem.actionNameId,
    footer: historyItem.globalFootnotes.map((r) => ({
      text: r.footnote,
      optionId: toFootnoteAppear(r.footnoteAppearOptionId) ?? FootnoteAppear.First,
    })),
    structure: {
      header: _(historyItem.structureHeaders)
        .map((r) => toStructureHeader(r.headerOptionId))
        .compact()
        .value(),
      footer: _(historyItem.structureFooters)
        .map((r) => toStructureFooter(r.footerOptionId))
        .compact()
        .value(),
      displayObjection: true,
    },
  };

  return result;
};
